import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link, useLocation, useSearch } from "wouter";
import { apiRequest } from "@/lib/queryClient";

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, "At least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setSubmitError(null);
    try {
      await apiRequest("POST", "/api/auth/reset-password", {
        token,
        newPassword: values.newPassword,
      });
      setSucceeded(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Couldn't reset your password. The link may have expired.",
      );
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-muted/20 py-10 px-4 flex items-center justify-center">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle>Missing reset link</CardTitle>
            <CardDescription>
              This page needs a valid reset token. Request a new link to continue.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-2 items-stretch">
            <Link href="/forgot-password" className="text-sm text-primary hover:underline">
              Request a reset link
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:underline">
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex items-center justify-center">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>
            {succeeded
              ? "Your password has been updated."
              : "Pick a password at least 8 characters long."}
          </CardDescription>
        </CardHeader>
        {succeeded ? (
          <CardFooter className="flex flex-col gap-3 items-stretch">
            <Button onClick={() => setLocation("/login")}>Sign in</Button>
          </CardFooter>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {submitError && <p className="text-sm text-destructive">{submitError}</p>}
              </CardContent>
              <CardFooter className="flex flex-col gap-3 items-stretch">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? "Saving…" : "Reset password"}
                </Button>
                <p className="text-sm text-muted-foreground text-center">
                  <Link href="/login" className="text-primary hover:underline">
                    Back to sign in
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Form>
        )}
      </Card>
    </div>
  );
}
